import Box from "@/elements/Box";
import Button from "@/elements/Button";
import CoverLayout from "@/elements/CoverLayout";
import Typography from "@/elements/Typography";
import {
  Checkbox,
  Container,
  FormControlLabel,
  Grid,
  Paper,
} from "@mui/material";
import {
  Build,
  DeleteSweep,
  EmojiEvents,
  EnergySavingsLeaf,
  Opacity,
  Park,
} from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import {
  RichiestaPreventivoCreateResponseDto,
  RichiestaPreventivoCreateDto,
  RichiestaPreventivoTipo,
  TipoOlioLavorazioni,
} from "@/api";
import axios from "axios";

interface ResultsViewProps {
  result: RichiestaPreventivoCreateResponseDto;

  tipoRichiesta: RichiestaPreventivoCreateDto["tipo"];

  tipoOlioLavorazioni?: RichiestaPreventivoCreateDto["tipoOlioLavorazioni"];

  wantsContact: boolean;

  isUpdatingContact: boolean;

  onContactChange: (value: boolean) => void;

  onNewRequest: () => void;

  onContactSubmit: () => void;
}

type FormatterContext = {
  t: (key: string, options?: Record<string, unknown>) => string;
  locale: string;
  ndLabel: string;
};

function formatEuro(
  value: number | null | undefined,
  { t, ndLabel, locale }: FormatterContext
): string {
  if (value == null) return ndLabel;
  const formatted = value.toLocaleString(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return t("richiestaPreventivo.results.euro", { value: formatted });
}

function formatLitri(
  value: number | null | undefined,
  { t }: FormatterContext
): string | null {
  if (value == null) return null;
  return t("richiestaPreventivo.results.litri", {
    value: value.toFixed(0),
  });
}

interface SavingSectionProps {
  step: number;
  title: string;
  accentColor: string;
  icon: React.ReactNode;
  primaryValue: string;
  primarySublabel?: string;
  secondaryValue?: string | null;
  secondarySublabel?: string;
}

function SavingSection({
  step,
  title,
  accentColor,
  icon,
  primaryValue,
  primarySublabel,
  secondaryValue,
  secondarySublabel,
}: SavingSectionProps) {
  return (
    <Paper elevation={2} sx={{ mb: 3, overflow: "hidden", borderRadius: 2 }}>
      <Box display="flex">
        <Box sx={{ width: 6, bgcolor: accentColor, flexShrink: 0 }} />
        <Box sx={{ flex: 1, p: 3 }}>
          <Box display="flex" alignItems="center" gap={1.5} mb={2.5}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: "50%",
                bgcolor: accentColor + "18",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: accentColor,
                flexShrink: 0,
              }}
            >
              {icon}
            </Box>
            <Box>
              <Typography
                variant="subtitle1"
                fontWeight="bold"
                sx={{ color: "#333", lineHeight: 1.2 }}
              >
                {title}
              </Typography>
            </Box>
          </Box>

          <Box>
            <Typography
              variant="h3"
              fontWeight="bold"
              sx={{ color: accentColor }}
            >
              {primaryValue}
            </Typography>
            {primarySublabel && (
              <Typography
                variant="caption"
                sx={{ color: "#999", display: "block", mt: 0.5 }}
              >
                {primarySublabel}
              </Typography>
            )}
            {secondaryValue && (
              <Box mt={2} pt={2} sx={{ borderTop: "1px solid #f0f0f0" }}>
                <Typography
                  variant="h5"
                  fontWeight="bold"
                  sx={{ color: "#555" }}
                >
                  {secondaryValue}
                </Typography>
                {secondarySublabel && (
                  <Typography
                    variant="caption"
                    sx={{ color: "#999", display: "block", mt: 0.5 }}
                  >
                    {secondarySublabel}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Paper>
  );
}

export function ResultsView({
  result,
  tipoRichiesta,
  tipoOlioLavorazioni,
  wantsContact,
  isUpdatingContact,
  onContactChange,
  onNewRequest,
  onContactSubmit,
}: ResultsViewProps) {
  const { t, i18n } = useTranslation();
  const nd = t("richiestaPreventivo.results.nd");
  const formatter = { t, locale: i18n.language, ndLabel: nd };

  const anni = result.orizzonteRisparmioAnni ?? 3;
  const anniLabel = t("richiestaPreventivo.results.anniLabel", { count: anni });

  const isLubrifica = tipoRichiesta === RichiestaPreventivoTipo.lubrifica;
  const isLubrorefrigerante =
    tipoOlioLavorazioni === TipoOlioLavorazioni.lubrorefrigerante;

  // Gestisce il download del PDF
  const handleDownloadPdf = async () => {
    if (!result.tokenRecupero || !result.filePdf) return;

    try {
      const response = await axios.get(
        `/api/richieste_preventivo/token/${result.tokenRecupero}/file_pdf`,
        { responseType: "blob" },
      );

      // Crea un URL temporaneo per il blob e avvia il download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const defaultFileName = t("richiestaPreventivo.results.pdfFileName");
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        result.filePdf.fileName || defaultFileName
      );
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Errore durante il download del PDF:", error);
    }
  };

  // Block 1 – RISPARMIO FLUIDO / OLIO
  let fluidoValue: string;
  let fluidoSublabel: string;
  let fluidoSecondary: string | null = null;
  let fluidoSecondarySublabel: string | undefined;

  // Block 1.5 – RISPARMIO SMALTIMENTO (solo lubrorefrigerante)
  let smaltimentoValue = "";
  let smaltimentoSublabel: string | undefined;
  let smaltimentoSecondary: string | null = null;
  let smaltimentoSecondarySublabel: string | undefined;
  let smaltimentoNA = true;

  // Block 2 – RISPARMIO UTENSILI
  let utensiliValue = "";
  let utensiliSublabel: string | undefined;
  let utensiliNA = false;

  // Block 3 – RISPARMIO TOTALE
  let totaleValue: string;
  let totaleSublabel: string;

  // Environmental
  let co2Value: string;
  let alberiValue: string | number;
  let co2Label: string;
  let alberiLabel: string;

  if (isLubrifica) {
    fluidoValue = formatEuro(result.risparmioEconomicoLubrifica, formatter);
    fluidoSublabel = t("richiestaPreventivo.results.risparmioEconomico", {
      anni,
      anniLabel,
    });
    fluidoSecondary = formatLitri(result.olioNonAcquistato, formatter);
    fluidoSecondarySublabel = t(
      "richiestaPreventivo.results.olioNonAcquistato",
      { anni, anniLabel },
    );
    utensiliNA = true;
    totaleValue = formatEuro(result.risparmioEconomicoLubrifica, formatter);
    totaleSublabel = t("richiestaPreventivo.results.risparmioEconomico", {
      anni,
      anniLabel,
    });
    co2Value = result.co2Risparmiata?.toFixed(0) ?? nd;
    alberiValue = result.numeriAlberiSalvati ?? nd;
    co2Label = t("richiestaPreventivo.results.co2", { anni, anniLabel });
    alberiLabel = t("richiestaPreventivo.results.alberi");
  } else if (isLubrorefrigerante) {
    fluidoValue = formatEuro(result.risparmioEconomicoFluido, formatter);
    fluidoSublabel = t("richiestaPreventivo.results.risparmioAcquistoFluido");
    fluidoSecondary = formatLitri(result.litriNonAcquistatiLavorazioni, formatter);
    fluidoSecondarySublabel = t(
      "richiestaPreventivo.results.litriNonAcquistati",
      { anni, anniLabel },
    );
    smaltimentoValue = formatEuro(result.risparmioSmaltimento, formatter);
    smaltimentoSublabel = t("richiestaPreventivo.results.risparmioSmaltimento");
    smaltimentoSecondary = formatLitri(result.litriEmulsioneNonSmaltita, formatter);
    smaltimentoSecondarySublabel = t(
      "richiestaPreventivo.results.litriEmulsioneNonSmaltita",
      { anni, anniLabel },
    );
    smaltimentoNA = false;
    utensiliValue = formatEuro(result.risparmioUtensili, formatter);
    utensiliSublabel = t("richiestaPreventivo.results.risparmioUtensili");
    totaleValue = formatEuro(
      result.risparmioEconomicoLavorazioniMeccaniche,
      formatter,
    );
    totaleSublabel = t("richiestaPreventivo.results.risparmioTotale", {
      anni,
      anniLabel,
    });
    co2Value = result.co2EvitataLavorazioni?.toFixed(0) ?? nd;
    alberiValue = result.equivalenzaAlberiLavorazioni ?? nd;
    co2Label = t("richiestaPreventivo.results.co2Lavorazioni", {
      anni,
      anniLabel,
    });
    alberiLabel = t("richiestaPreventivo.results.alberiLavorazioni");
  } else {
    // olioIntero – no separate fluid/tool economic breakdown
    fluidoValue =
      formatLitri(result.litriNonAcquistatiLavorazioni, formatter) ?? nd;
    fluidoSublabel = t("richiestaPreventivo.results.litriNonAcquistati", {
      anni,
      anniLabel,
    });
    utensiliNA = true;
    totaleValue = formatEuro(
      result.risparmioEconomicoLavorazioniMeccaniche,
      formatter,
    );
    totaleSublabel = t("richiestaPreventivo.results.risparmioTotaleOlio");
    co2Value = result.co2EvitataLavorazioni?.toFixed(0) ?? nd;
    alberiValue = result.equivalenzaAlberiLavorazioni ?? nd;
    co2Label = t("richiestaPreventivo.results.co2Lavorazioni", {
      anni,
      anniLabel,
    });
    alberiLabel = t("richiestaPreventivo.results.alberiLavorazioni");
  }

  return (
    <CoverLayout bigContent noBackground>
      <Container maxWidth="md">
        <Box sx={{ py: 4, pb: 28 }}>
          {/* Header */}
          <Box textAlign="center" mb={3}>
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                bgcolor: "#4caf50",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 1.5,
              }}
            >
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none">
                <path
                  d="M4 13l5 5L20 7"
                  stroke="white"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Box>
          </Box>

          {/* Contact Request – in alto per massima visibilità */}
          <Paper
            elevation={4}
            sx={{
              mb: 4,
              borderRadius: 3,
              overflow: "hidden",
              border: wantsContact ? "2px solid #E7B142" : "2px solid #f5dfa0",
              transition: "border-color 0.2s",
            }}
          >
            <Box sx={{ p: 3 }}>
              <Typography variant="body1" sx={{ color: "#333", mb: 2.5 }}>
                {t("richiestaPreventivo.results.contactSubtitle")}
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={wantsContact}
                    onChange={(e) => onContactChange(e.target.checked)}
                    sx={{
                      color: "#E7B142",
                      "&.Mui-checked": { color: "#E7B142" },
                      transform: "scale(1.3)",
                      mr: 0.5,
                    }}
                  />
                }
                label={
                  <Typography
                    variant="body1"
                    fontWeight="bold"
                    sx={{ color: "#333" }}
                  >
                    {t("richiestaPreventivo.results.contactCheckbox")}
                  </Typography>
                }
              />
            </Box>
          </Paper>

          <Typography
            variant="body1"
            sx={{ color: "#888", textAlign: "center" }}
            mb={1.5}
          >
            {isLubrifica
              ? t("richiestaPreventivo.results.lubrificaResultsTitle")
              : t("richiestaPreventivo.results.resultsTitle", {
                  anni,
                  anniLabel,
                })}
          </Typography>

          {isLubrifica ? (
            <>
              {/* Lubrifica – layout dedicato */}
              <Paper
                elevation={2}
                sx={{ mb: 3, borderRadius: 2, overflow: "hidden" }}
              >
                <Box sx={{ p: 3 }}>
                  {/* Olio non acquistato */}
                  <Box sx={{ mb: 2.5, pb: 2.5, borderBottom: "1px solid #f0f0f0" }}>
                    <Typography variant="h3" fontWeight="bold" sx={{ color: "#1976d2" }}>
                      {formatLitri(result.olioNonAcquistato, formatter) ?? nd}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "#666", display: "block", mt: 0.5 }}
                    >
                      {t("richiestaPreventivo.results.lubrificaOlioLabel", {
                        anni,
                        anniLabel,
                      })}
                    </Typography>
                  </Box>

                  {/* Risparmio economico */}
                  <Box sx={{ mb: 2.5, pb: 2.5, borderBottom: "1px solid #f0f0f0" }}>
                    <Typography variant="h3" fontWeight="bold" sx={{ color: "#2e7d32" }}>
                      {formatEuro(result.risparmioEconomicoLubrifica, formatter)}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: "#666", display: "block", mt: 0.5 }}
                    >
                      {t(
                        "richiestaPreventivo.results.lubrificaRisparmioLabel",
                        { anni, anniLabel },
                      )}
                    </Typography>
                  </Box>

                  {/* Risparmio pompe */}
                  {(result.risparmioEconomicoPompe ?? 0) > 0 && (
                    <Box sx={{ mb: 2.5, pb: 2.5, borderBottom: "1px solid #f0f0f0" }}>
                      <Typography variant="h3" fontWeight="bold" sx={{ color: "#2e7d32" }}>
                        {formatEuro(result.risparmioEconomicoPompe, formatter)}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "#666", display: "block", mt: 0.5 }}
                      >
                        {t("richiestaPreventivo.results.risparmioPompe")}
                      </Typography>
                    </Box>
                  )}

                  {/* Risparmio valvole */}
                  {(result.risparmioEconomicoValvole ?? 0) > 0 && (
                    <Box sx={{ mb: 2.5, pb: 2.5, borderBottom: "1px solid #f0f0f0" }}>
                      <Typography variant="h3" fontWeight="bold" sx={{ color: "#2e7d32" }}>
                        {formatEuro(result.risparmioEconomicoValvole, formatter)}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "#666", display: "block", mt: 0.5 }}
                      >
                        {t("richiestaPreventivo.results.risparmioValvole")}
                      </Typography>
                    </Box>
                  )}

                  {/* Risparmio fermo macchina */}
                  {(result.risparmioEconomicoFermoMacchina ?? 0) > 0 && (
                    <Box sx={{ mb: 2.5, pb: 2.5, borderBottom: "1px solid #f0f0f0" }}>
                      <Typography variant="h3" fontWeight="bold" sx={{ color: "#2e7d32" }}>
                        {formatEuro(result.risparmioEconomicoFermoMacchina, formatter)}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "#666", display: "block", mt: 0.5 }}
                      >
                        {t("richiestaPreventivo.results.risparmioFermoMacchina")}
                      </Typography>
                    </Box>
                  )}

                  {/* CO₂ evitata */}
                  <Box>
                    <Box display="flex" alignItems="baseline" gap={0.5}>
                      <Typography
                        variant="h3"
                        fontWeight="bold"
                        sx={{ color: "#388e3c" }}
                      >
                        {result.co2Risparmiata?.toFixed(0) ?? nd}
                      </Typography>
                      <Typography variant="body1" sx={{ color: "#558b2f" }}>
                        {t("richiestaPreventivo.results.co2Unit")}
                      </Typography>
                    </Box>
                    <Typography
                      variant="caption"
                      sx={{ color: "#666", display: "block", mt: 0.5 }}
                    >
                      {t("richiestaPreventivo.results.lubrificaCo2Label", {
                        anni,
                        anniLabel,
                      })}
                    </Typography>
                  </Box>
                </Box>
              </Paper>

              {/* Frase riassuntiva */}
              <Paper
                elevation={0}
                sx={{
                  mb: 3,
                  p: 3,
                  borderRadius: 2,
                  bgcolor: "#e8f5e9",
                  border: "1px solid #c8e6c9",
                }}
              >
                <Box display="flex" alignItems="flex-start" gap={1}>
                  <EnergySavingsLeaf
                    sx={{
                      color: "#388e3c",
                      fontSize: 20,
                      mt: 0.3,
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    variant="body1"
                    sx={{ color: "#2e7d32", fontStyle: "italic" }}
                  >
                    {t("richiestaPreventivo.results.lubrificaSummary", {
                      anni,
                      anniLabel,
                      litri: result.olioNonAcquistato?.toFixed(0) ?? nd,
                      co2: result.co2Risparmiata?.toFixed(0) ?? nd,
                    })}
                  </Typography>
                </Box>
              </Paper>
            </>
          ) : (
            <>
              {/* Block 1 – Risparmio Fluido / Olio */}
              <SavingSection
                step={1}
                title={t("richiestaPreventivo.results.sectionFluido")}
                accentColor="#1976d2"
                icon={<Opacity fontSize="small" />}
                primaryValue={fluidoValue}
                primarySublabel={fluidoSublabel}
                secondaryValue={fluidoSecondary}
                secondarySublabel={fluidoSecondarySublabel}
              />

              {/* Block 1.5 – Risparmio Smaltimento (solo lubrorefrigerante) */}
              {!smaltimentoNA && (
                <SavingSection
                  step={2}
                  title={t("richiestaPreventivo.results.sectionSmaltimento")}
                  accentColor="#e91e8c"
                  icon={<DeleteSweep fontSize="small" />}
                  primaryValue={smaltimentoValue}
                  primarySublabel={smaltimentoSublabel}
                  secondaryValue={smaltimentoSecondary}
                  secondarySublabel={smaltimentoSecondarySublabel}
                />
              )}

              {/* Block 2 – Risparmio Utensili (solo se disponibile) */}
              {!utensiliNA && (
                <SavingSection
                  step={3}
                  title={t("richiestaPreventivo.results.sectionUtensili")}
                  accentColor="#7b1fa2"
                  icon={<Build fontSize="small" />}
                  primaryValue={utensiliValue}
                  primarySublabel={utensiliSublabel}
                />
              )}

              {/* Block 2.5 – Risparmio Pompe Alta Pressione (A3, solo se valorizzato) */}
              {(result.risparmioEconomicoPompeAltaPressione ?? 0) > 0 && (
                <SavingSection
                  step={4}
                  title={t("richiestaPreventivo.results.risparmioPompeAltaPressione")}
                  accentColor="#d32f2f"
                  icon={<Build fontSize="small" />}
                  primaryValue={formatEuro(result.risparmioEconomicoPompeAltaPressione, formatter)}
                />
              )}

              {/* Block 3 – Risparmio Totale Stimato */}
              <Paper
                elevation={3}
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  background:
                    "linear-gradient(135deg, #2e7d32 0%, #43a047 100%)",
                  overflow: "hidden",
                }}
              >
                <Box sx={{ p: 3 }}>
                  <Box display="flex" alignItems="center" gap={1.5} mb={2}>
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "50%",
                        bgcolor: "rgba(255,255,255,0.15)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <EmojiEvents htmlColor="white" sx={{ fontSize: 22 }} />
                    </Box>
                    <Box>
                      <Typography
                        variant="subtitle1"
                        fontWeight="bold"
                        sx={{ color: "white", lineHeight: 1.2 }}
                        color="white"
                      >
                        {t("richiestaPreventivo.results.sectionTotale")}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography
                    variant="h2"
                    fontWeight="bold"
                    sx={{ color: "white" }}
                    color="white"
                  >
                    {totaleValue}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: "rgba(255,255,255,0.7)", mt: 0.5 }}
                  >
                    {totaleSublabel}
                  </Typography>
                </Box>
              </Paper>

              {/* Environmental Impact */}
              <Paper
                elevation={0}
                sx={{
                  mb: 3,
                  p: 3,
                  borderRadius: 2,
                  bgcolor: "#f1f8e9",
                  border: "1px solid #c8e6c9",
                }}
              >
                <Box display="flex" alignItems="center" gap={1} mb={2.5}>
                  <EnergySavingsLeaf sx={{ color: "#388e3c", fontSize: 22 }} />
                  <Typography
                    variant="subtitle2"
                    fontWeight="bold"
                    sx={{
                      color: "#388e3c",
                      textTransform: "uppercase",
                      letterSpacing: 1.2,
                    }}
                  >
                    {t("richiestaPreventivo.results.sectionAmbientale")}
                  </Typography>
                </Box>
                <Box display="flex" gap={4} flexWrap="wrap">
                  <Box>
                    <Box display="flex" alignItems="baseline" gap={0.5}>
                      <Typography
                        variant="h4"
                        fontWeight="bold"
                        sx={{ color: "#2e7d32" }}
                      >
                        {co2Value}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#558b2f" }}>
                        {t("richiestaPreventivo.results.co2Unit")}
                      </Typography>
                    </Box>
                    <Typography variant="caption" sx={{ color: "#666" }}>
                      {co2Label}
                    </Typography>
                  </Box>
                  <Box>
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <Park sx={{ color: "#2e7d32", fontSize: 20 }} />
                      <Typography
                        variant="h4"
                        fontWeight="bold"
                        sx={{ color: "#2e7d32" }}
                      >
                        {alberiValue}
                      </Typography>
                    </Box>
                    <Typography variant="caption" sx={{ color: "#666" }}>
                      {alberiLabel}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </>
          )}

          {/* Disclaimer */}
          <Typography
            variant="caption"
            sx={{
              display: "block",
              color: "#aaa",
              fontStyle: "italic",
              textAlign: "center",
              mb: 3,
              px: 1,
            }}
          >
            * {t("richiestaPreventivo.results.disclaimer")}
          </Typography>
        </Box>
      </Container>

      {/* Fixed Bottom Bar */}
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          bgcolor: "#ffffff",
          borderTop: "1px solid #e0e0e0",
          boxShadow: "0px -4px 12px rgba(0, 0, 0, 0.15)",
          zIndex: 100,
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ py: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Button
                  variant="outlined"
                  color="secondary"
                  fullWidth
                  size="large"
                  onClick={onNewRequest}
                  disabled={isUpdatingContact}
                >
                  {t("richiestaPreventivo.results.newRequest")}
                </Button>
              </Grid>
              <Grid item xs={12} md={6}>
                <Button
                  variant="contained"
                  color="secondary"
                  fullWidth
                  size="large"
                  onClick={onContactSubmit}
                  disabled={isUpdatingContact}
                >
                  {t("richiestaPreventivo.results.confirm")}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Container>
      </Box>
    </CoverLayout>
  );
}
